import React, { memo, useCallback, useEffect, useState } from 'react'
import { BdaServiceProperty, BdaServiceTuningProperty, TuningPropertyMapping } from '../../models/BdaService';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Button } from 'react-bootstrap';
import { Checkbox, Flex, Form, Input, Modal, Select } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MappingsEditor } from './MappingsEditor';
import { getPropertyIcon } from '../../utils';
import ScriptEditor from '../common/ScriptEditor';

const types = [
  { label: 'Application Property', value: 'application' },
  { label: 'Tuning Property', value: 'tuning' },
  { label: 'Static Property', value: 'static' },
].map(type => ({ ...type, icon: getPropertyIcon(type.value as "application" | "tuning" | "static") }));

type ModalProps = {
  open: boolean;
  onClose: VoidFunction;
  property: BdaServiceProperty;
  onSubmit: (values: any) => void;
}

const ServicePropertyModal = memo(({ open, onClose, onSubmit, property }: ModalProps) => {
  const [form] = Form.useForm();
  const [mappings, setMappings] = useState<TuningPropertyMapping[]>();
  const [defaultValue, setDefaultValue] = useState<string | undefined>();

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(property);
    if (property.type === "tuning") setMappings((property as BdaServiceTuningProperty).mappings);
    if (property.valueType === "JSON") setDefaultValue(property.defaultValue || "");
  }, [form, property]);

  const handleMappingsChange = (newMappings: TuningPropertyMapping[]) => setMappings(newMappings);

  const handleSubmit = (values: any) => {
    if (values.type === 'tuning') values.mappings = mappings || [];
    if (values.valueType === 'JSON') values.defaultValue = defaultValue;
    onSubmit(values);
  }

  const handleDefaultValueChange = useCallback((value?: string) => setDefaultValue(value || ""), []);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onClose={onClose}
      style={{ top: 20 }}
      title="New Property"
      footer={null}
    >
      <Form form={form} layout="vertical" colon={false} onFinish={handleSubmit} className="mt-3">
        <Form.Item
          name="key"
          label="Key"
          rules={[{ required: true, message: "Property Key is required" }]}
        >
          <Input placeholder="Property key" />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <Input.TextArea placeholder="Property description" rows={3} />
        </Form.Item>

        <Form.Item name="type" label="Type">
          <Select className="w-100" placeholder="Select Property Type">
            {types.map((option, index) => (
              <Select.Option key={index} value={option.value} label={option.label}>
                <FontAwesomeIcon icon={option.icon} fixedWidth />
                <span className="ms-2">{option.label}</span>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Flex align="center">
          <Form.Item name="mandatory" valuePropName="checked">
            <Checkbox>Mandatory</Checkbox>
          </Form.Item>

          <Form.Item className="ms-3" name="invisible" valuePropName="checked">
            <Checkbox>Invisible</Checkbox>
          </Form.Item>
        </Flex>

        <Form.Item name="valueType" label="Value Type">
          <Select className="w-100" placeholder="Select Property Value Type">
            <Select.Option value="STRING">String</Select.Option>
            <Select.Option value="BOOLEAN">Boolean</Select.Option>
            <Select.Option value="INT">Integer</Select.Option>
            <Select.Option value="DOUBLE">Double</Select.Option>
            <Select.Option value="JSON">JSON</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item noStyle dependencies={['valueType']}>
          {({ getFieldValue }) => {
            const valueType = getFieldValue('valueType') as BdaServiceProperty['valueType'];
            if (valueType === 'BOOLEAN') return (
              <Form.Item name="defaultValue" valuePropName="checked">
                <Checkbox>Default Value</Checkbox>
              </Form.Item>
            )
            else if (valueType === 'JSON') return (
              <>
                <h6 className="mb-1">Default Value</h6>
                <ScriptEditor value={defaultValue} onChange={handleDefaultValueChange} />
              </>
            )
            else return (
              <Form.Item name="defaultValue" label="Default Value">
                <Input placeholder="Property default value" step={valueType === 'DOUBLE' ? 0.01 : 1} />
              </Form.Item>
            )
          }}
        </Form.Item>

        <Form.Item noStyle dependencies={['type']}>
          {({ getFieldValue }) => {
            const type: "application" | "tuning" | "static" = getFieldValue('type');

            return type === "application"
              ? (
                <Flex align="center">
                  <Form.Item name="inputData" valuePropName="checked">
                    <Checkbox>Input Data</Checkbox>
                  </Form.Item>
                  <Form.Item className="ms-3" name="outputData" valuePropName="checked">
                    <Checkbox>Output Data</Checkbox>
                  </Form.Item>
                </Flex>
              )
              : type === "tuning"
                ? (
                  <>
                    <Form.Item name="measure" label="Measure">
                      <Input placeholder="Property Measure" />
                    </Form.Item>

                    <Form.Item name="category" valuePropName="checked">
                      <Checkbox>Category</Checkbox>
                    </Form.Item>

                    <Form.Item name="minValue" label="Min Value">
                      <Input placeholder="Property Min Value" />
                    </Form.Item>

                    <Form.Item name="maxValue" label="Max Value">
                      <Input placeholder="Property Max Value" />
                    </Form.Item>

                    <h6>Mappings</h6>
                    <MappingsEditor mappings={mappings || []} onMappingsChange={handleMappingsChange} />
                  </>
                )
                : (
                  <>
                    {/* <Form.Item name="externalized" valuePropName="checked">
                      <Checkbox>Externalized</Checkbox>
                    </Form.Item>

                    <Form.Item noStyle dependencies={['externalized']}>
                      {({ getFieldValue }) => getFieldValue('externalized') && (
                        <Form.Item name="url" label="Url" rules={[{ required: true, message: "Property URL is required when property is externalized" }]}>
                          <Input placeholder="Property URL" />
                        </Form.Item>
                      )}
                    </Form.Item> */}
                  </>
                )
          }}
        </Form.Item>

        <Flex align="center" justify="space-between" className="mt-3">
          <Button variant="light" type="button" size="sm" className="border" onClick={onClose}>
            Close
          </Button>
          <Button variant="success" type="submit" size="sm">
            <FontAwesomeIcon icon={solid('check')} />
            <span className="ms-2">Confirm</span>
          </Button>
        </Flex>
      </Form>
    </Modal>
  )
})

export default ServicePropertyModal;